import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { CheckCircle, AlertTriangle, User, Trash2, Plus, Shield } from "lucide-react";
import moment from "moment";
import { updateIncidentSupervision, addIncidentSanction, removeIncidentSanction } from "../../api/packages";
import { showToast } from "../../utils/showToast";
import { apiErrorMessage } from "../../utils/apiErrorMessage";
import Spinner from "../spinner/spinner";

const EMPTY_SANCTION = { driverName: "", driverId: "", reason: "", amount: "" };

export default function SupervisionTab({ incident, onUpdated, readOnly }) {
  const { t } = useTranslation();
  const supervision = incident.supervision || {};
  const isResolved = supervision.resolved === true;
  const isRequired = supervision.required === true;
  const activeSanctions = (supervision.sanctions || []).filter((s) => !s.removedAt);
  const removedSanctions = (supervision.sanctions || []).filter((s) => !!s.removedAt);

  const [observations, setObservations] = useState(supervision.observations || "");
  const [savingObs, setSavingObs] = useState(false);
  const [resolving, setResolving] = useState(false);

  const [showSanctionForm, setShowSanctionForm] = useState(false);
  const [sanctionForm, setSanctionForm] = useState(EMPTY_SANCTION);
  const [addingSanction, setAddingSanction] = useState(false);
  const [removingId, setRemovingId] = useState(null);

  useEffect(() => {
    setObservations(incident.supervision?.observations || "");
  }, [incident]);

  const handleSaveObservations = async () => {
    setSavingObs(true);
    try {
      const response = await updateIncidentSupervision({
        incidentId: incident._id,
        observations,
      });
      if (response.success) {
        showToast(t("Observaciones guardadas"), true);
        onUpdated && onUpdated(response.data);
      } else {
        showToast(response.error?.message || t("Error al guardar"), false);
      }
    } catch (err) {
      showToast(apiErrorMessage(err, t("Error al guardar")), false);
    } finally {
      setSavingObs(false);
    }
  };

  const handleMarkResolved = async () => {
    if (!window.confirm(t("Marcar Supervisión como resuelta"))) return;
    setResolving(true);
    try {
      const response = await updateIncidentSupervision({
        incidentId: incident._id,
        observations,
        resolved: true,
      });
      if (response.success) {
        showToast(t("Supervisión marcada como resuelta"), true);
        onUpdated && onUpdated(response.data);
      } else {
        showToast(response.error?.message || t("Error al resolver"), false);
      }
    } catch (err) {
      showToast(apiErrorMessage(err, t("Error al resolver")), false);
    } finally {
      setResolving(false);
    }
  };

  const handleAddSanction = async (e) => {
    e.preventDefault();
    if (!sanctionForm.driverName.trim() || !sanctionForm.reason.trim() || sanctionForm.amount === "") {
      showToast(t("Completa todos los campos requeridos"), false);
      return;
    }
    setAddingSanction(true);
    try {
      const response = await addIncidentSanction({
        incidentId: incident._id,
        driverName: sanctionForm.driverName.trim(),
        driverId: sanctionForm.driverId.trim() || undefined,
        reason: sanctionForm.reason.trim(),
        amount: parseFloat(sanctionForm.amount),
      });
      if (response.success) {
        showToast(t("Sanción aplicada correctamente"), true);
        setSanctionForm(EMPTY_SANCTION);
        setShowSanctionForm(false);
        onUpdated && onUpdated(response.data);
      } else {
        showToast(response.error?.message || t("Error al aplicar sanción"), false);
      }
    } catch (err) {
      showToast(apiErrorMessage(err, t("Error al aplicar sanción")), false);
    } finally {
      setAddingSanction(false);
    }
  };

  const handleRemoveSanction = async (sanctionId) => {
    if (!window.confirm(t("¿Eliminar esta sanción? Quedará registrada como eliminada."))) return;
    setRemovingId(sanctionId);
    try {
      const response = await removeIncidentSanction({
        incidentId: incident._id,
        sanctionId,
      });
      if (response.success) {
        showToast(t("Sanción eliminada"), true);
        onUpdated && onUpdated(response.data);
      } else {
        showToast(response.error?.message || t("Error al eliminar sanción"), false);
      }
    } catch (err) {
      showToast(apiErrorMessage(err, t("Error al eliminar sanción")), false);
    } finally {
      setRemovingId(null);
    }
  };

  return (
    <div>
      {/* Banner de estado */}
      {isResolved ? (
        <div className="tab-section__resolved-banner">
          <CheckCircle size={16} />
          {t("Supervisión resuelta")}
          {supervision.resolvedAt && (
            <span style={{ marginLeft: "auto", fontSize: "0.78rem", fontWeight: 400 }}>
              {moment(supervision.resolvedAt).format("DD/MM/YYYY HH:mm")}
            </span>
          )}
        </div>
      ) : isRequired && (
        <div className="tab-section__pending-banner">
          <AlertTriangle size={16} />
          {t("Pendiente de resolución Supervisión")}
        </div>
      )}

      {/* Sanciones activas */}
      <div className="tab-section">
        <div className="tab-section__title" style={{ justifyContent: "space-between" }}>
          <span style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}>
            <Shield size={14} />
            {t("Descuentos / Sanciones")} ({activeSanctions.length})
          </span>
          {!isResolved && !readOnly && (
            <button
              type="button"
              className="btn-resolve btn-resolve--secondary btn-resolve--sm"
              onClick={() => setShowSanctionForm((v) => !v)}
            >
              <Plus size={14} />
              {t("Agregar descuento")}
            </button>
          )}
        </div>

        {activeSanctions.length === 0 && !showSanctionForm && (
          <p style={{ fontSize: "0.82rem", color: "#6c757d", fontStyle: "italic" }}>
            {t("No hay sanciones aplicadas.")}
          </p>
        )}

        {activeSanctions.map((s) => (
          <div key={s._id} className="sanction-card">
            <div className="sanction-card__header">
              <div className="sanction-card__driver">
                <User size={15} />
                {s.driverName}
              </div>
              <span className="sanction-card__amount">${Number(s.amount || 0).toLocaleString("es-CL")}</span>
            </div>
            <p className="sanction-card__detail"><strong>{t("Motivo:")} </strong> {s.reason}</p>
            {s.appliedAt && (
              <p className="sanction-card__detail">
                <strong>{t("Aplicado:")}</strong> {moment(s.appliedAt).format("DD/MM/YYYY HH:mm")}
                {s.appliedBy?.name && ` por ${s.appliedBy.name}`}
              </p>
            )}
            {!isResolved && !readOnly && (
              <div className="sanction-card__actions">
                <button
                  type="button"
                  className="btn-resolve btn-resolve--danger btn-resolve--sm"
                  onClick={() => handleRemoveSanction(s._id)}
                  disabled={removingId === s._id}
                >
                  {removingId === s._id ? <Spinner /> : <Trash2 size={13} />}
                  {t("Eliminar descuento")}
                </button>
              </div>
            )}
          </div>
        ))}

        {/* Formulario de nueva sanción */}
        {showSanctionForm && !isResolved && !readOnly && (
          <form className="sanction-form" onSubmit={handleAddSanction}>
            <div className="sanction-form__title">
              <Plus size={14} />
              {t("Nuevo descuento")}
            </div>
            <div className="sanction-form__grid">
              <div className="sanction-form__field">
                <label className="sanction-form__label">{t("Repartidor *")}</label>
                <input
                  className="sanction-form__input"
                  value={sanctionForm.driverName}
                  onChange={(e) => setSanctionForm((f) => ({ ...f, driverName: e.target.value }))}
                  placeholder={t("Nombre del repartidor")}
                  required
                />
              </div>
              <div className="sanction-form__field">
                <label className="sanction-form__label">{t("Monto a descontar ($) *")}</label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  className="sanction-form__input"
                  value={sanctionForm.amount}
                  onChange={(e) => setSanctionForm((f) => ({ ...f, amount: e.target.value }))}
                  placeholder="0"
                  required
                />
              </div>
              <div className="sanction-form__field sanction-form__field--full">
                <label className="sanction-form__label">{t("Motivo de la sanción *")}</label>
                <input
                  className="sanction-form__input"
                  value={sanctionForm.reason}
                  onChange={(e) => setSanctionForm((f) => ({ ...f, reason: e.target.value }))}
                  placeholder={t("Descripción del motivo")}
                  required
                />
              </div>
            </div>
            <div className="sanction-form__actions">
              <button
                type="button"
                className="btn-resolve btn-resolve--outline btn-resolve--sm"
                onClick={() => { setShowSanctionForm(false); setSanctionForm(EMPTY_SANCTION); }}
              >
                {t("Cancelar")}
              </button>
              <button
                type="submit"
                className="btn-resolve btn-resolve--primary btn-resolve--sm"
                disabled={addingSanction}
              >
                {addingSanction ? <Spinner /> : <CheckCircle size={13} />}
                {t("Aplicar descuento")}
              </button>
            </div>
          </form>
        )}

        {/* Sanciones eliminadas (historial) */}
        {removedSanctions.length > 0 && (
          <div style={{ marginTop: "0.75rem" }}>
            <div className="tab-section__title" style={{ fontSize: "0.72rem", color: "#adb5bd" }}>
              Sanciones eliminadas ({removedSanctions.length})
            </div>
            {removedSanctions.map((s) => (
              <div key={s._id} className="sanction-card sanction-card--removed">
                <div className="sanction-card__header">
                  <div className="sanction-card__driver">
                    <User size={14} />
                    {s.driverName}
                    <span className="sanction-card__removed-badge">{t("ELIMINADA")}</span>
                  </div>
                  <span style={{ fontSize: "0.85rem", color: "#6c757d", textDecoration: "line-through" }}>
                    ${Number(s.amount || 0).toLocaleString("es-CL")}
                  </span>
                </div>
                <p className="sanction-card__detail">{s.reason}</p>
                {s.removedAt && (
                  <p className="sanction-card__detail">
                    {t("Eliminada el")} {moment(s.removedAt).format("DD/MM/YYYY HH:mm")}
                  </p>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Observaciones */}
      <div className="tab-section">
        <div className="tab-section__title">{t("Observaciones")}</div>
        <textarea
          className="comment-textarea"
          value={observations}
          onChange={(e) => setObservations(e.target.value)}
          placeholder={t("Ej: No corresponde descuento, fue error del comprador.")}
          rows={3}
          disabled={isResolved || readOnly}
        />
        {!isResolved && !readOnly && (
          <div style={{ display: "flex", gap: "0.6rem", marginTop: "0.5rem", flexWrap: "wrap" }}>
            <button
              type="button"
              className="btn-resolve btn-resolve--outline"
              onClick={handleSaveObservations}
              disabled={savingObs || resolving}
            >
              {savingObs ? <Spinner /> : null}
              {t("Guardar observaciones")}
            </button>
            <button
              type="button"
              className="btn-resolve btn-resolve--primary"
              onClick={handleMarkResolved}
              disabled={savingObs || resolving}
              style={{ marginLeft: "auto" }}
            >
              {resolving ? <Spinner /> : <CheckCircle size={16} />}
              {t("Marcar Supervisión como resuelta")}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
